import { FloatingHint } from "@rapid/components/FloatingHint";
import { useMemo, useState, useEffect, useCallback } from "react";
import { BannerProgressHint, AbortUIConfig } from "@rapid/components/BannerProgressHint";
import {
    useGetInferenceQueueItemById,
    InferenceStatus,
    InferenceAbortData,
    useCurrentMedia
} from "@rapid/store";
import { useCurrentWorkflow } from "@rapid/hooks/useCurrentWorkflow";
import { useCurrentWorkspace } from "@rapid/hooks/useCurrentWorkspace";
import { MediaItemUuid } from "@rapid/types/media";
import { useStopGeneratingPredictions } from "@rapid/hooks/useStopGeneratingPredictions";
import { useGetPredictions } from "@rapid/hooks/useGetPredictions";
import { useFlow } from "@rapid/contexts/FlowContext";
import { useFilePreview } from "@rapid/contexts/FilePreviewContext";
import { useFlowState } from "@rapid/hooks/useFlowState";
import { Flows } from "@rapid/types/flows";
import { InferenceTypes } from "@functions/rapid/services/inference/stream/types";

// Determine UI messaging based on error categories from abort data
function getAbortUIConfig(abortData?: InferenceAbortData): AbortUIConfig {
    if (!abortData?.errorCounts) {
        return {
            message: "We had an error processing this file.",
            subMessage: "Do you want to retry?",
            allowRetry: true
        };
    }

    const { errorCounts } = abortData;

    // Check for 5xx server errors
    if (errorCounts["5xx_server_error"] > 0) {
        return {
            message: "Our servers are currently busy.",
            subMessage: "Please try again in a few minutes.",
            allowRetry: true
        };
    }

    // Check for rate limiting
    if (errorCounts["429_rate_limited"] > 0) {
        return {
            message: "You've hit the rate limit.",
            subMessage: "Please wait a moment before trying again.",
            allowRetry: true
        };
    }

    // Check for 4xx client errors (bad image data, corrupted video, etc.)
    if (errorCounts["4xx_client_error"] > 0) {
        return {
            message: "This file appears to be corrupted or invalid.",
            subMessage: "Try uploading a different file.",
            allowRetry: false
        };
    }

    // Check for connection errors
    if (errorCounts["connection_error"] > 0) {
        return {
            message: "Connection error occurred.",
            subMessage: "Check your internet connection and retry.",
            allowRetry: true
        };
    }

    // Default/unknown errors - allow retry
    return {
        message: "We had an error processing this file.",
        subMessage: "Do you want to retry?",
        allowRetry: true
    };
}

export const InferenceProgressHint = ({
    fileId,
    rotatingContent
}: {
    fileId: MediaItemUuid;
    rotatingContent?: string[];
}) => {
    const inferenceQueueItem = useGetInferenceQueueItemById(fileId);
    const { stopGeneratingPredictions } = useStopGeneratingPredictions();
    const { generateModel, getPredictionsForFile } = useGetPredictions();
    const currentMedia = useCurrentMedia();
    const { videoControls } = useFilePreview();
    const { flow } = useFlowState();
    const { currentWorkflow: workflow } = useCurrentWorkflow();
    const { apiKey } = useCurrentWorkspace();
    const [lastProgress, setLastProgress] = useState(0);
    const [showHint, setShowHint] = useState(false);
    const [isStopped, setIsStopped] = useState(false);
    const [isAborted, setIsAborted] = useState(false);

    // Track if we ever had an inference item
    useEffect(() => {
        if (inferenceQueueItem) {
            setShowHint(true);
            setLastProgress(inferenceQueueItem.progress || 0);
            // If we have new inference data and it's running, reset states
            if (inferenceQueueItem.status === InferenceStatus.INFERRING) {
                setIsStopped(false);
                setIsAborted(false);
            }
        }
    }, [inferenceQueueItem]);

    // Hide when completed, show aborted state when aborted
    useEffect(() => {
        if (inferenceQueueItem?.status === InferenceStatus.COMPLETED) {
            setShowHint(false);
            setIsStopped(false);
            setIsAborted(false);
        } else if (inferenceQueueItem?.status === InferenceStatus.ABORTED) {
            setIsAborted(true);
            setIsStopped(false);

            // Pause video when aborted
            if (videoControls?.videoRef?.current) {
                const videoElement = videoControls.videoRef.current;
                videoElement.pause();
                videoControls.onPauseClicked(videoElement.currentTime);
            }
        }
    }, [inferenceQueueItem?.status, videoControls]);

    const handleStop = useCallback(() => {
        setIsStopped(true);
        stopGeneratingPredictions();

        // Pause video directly
        if (videoControls?.videoRef?.current) {
            const videoElement = videoControls.videoRef.current;
            const currentTime = videoElement.currentTime;
            console.log("[InferenceProgressHint] Pausing video at time:", currentTime);

            // Directly pause the video element
            videoElement.pause();

            // Also call the pause callback to update state
            videoControls.onPauseClicked(currentTime);
        }
    }, [stopGeneratingPredictions, videoControls]);

    const handleStart = useCallback(async () => {
        setIsStopped(false);
        setIsAborted(false);

        // Resume video if it was paused
        if (videoControls?.videoRef?.current) {
            const videoElement = videoControls.videoRef.current;
            console.log("[InferenceProgressHint] Resuming video");

            // Directly play the video element
            videoElement.play().catch((err) => {
                console.error("[InferenceProgressHint] Failed to play video:", err);
            });

            // Also call the play callback to update state
            if (videoControls.onPlayClicked) {
                videoControls.onPlayClicked();
            }
        }

        try {
            // Different restart logic based on flow
            if (flow === Flows.DEPLOY || flow === Flows.SHARE) {
                // For deploy/share flows, use workflow inference directly
                if (!currentMedia?.file) {
                    console.warn("[InferenceProgressHint] No current media file");
                    return;
                }

                console.log("[InferenceProgressHint] Restarting workflow inference");
                const abortController = new AbortController();

                const inferenceType =
                    flow === Flows.DEPLOY
                        ? InferenceTypes.WORKFLOW
                        : InferenceTypes.WORKFLOW_PUBLIC;

                await getPredictionsForFile({
                    mediaItem: currentMedia.file,
                    abortController,
                    useInferenceQueue: true,
                    inferenceType
                });
            } else {
                // For build flow, use generateModel (requires annotations)
                console.log("[InferenceProgressHint] Restarting model generation");
                await generateModel();
            }
        } catch (error) {
            console.error("Failed to restart predictions:", error);
        }
    }, [generateModel, getPredictionsForFile, videoControls, flow, currentMedia, workflow, apiKey]);

    const hint = useMemo(() => {
        if (showHint) {
            const props: {
                progress: number;
                onStop: () => void;
                onRestart: () => void;
                rotatingContent?: string[];
                isStopped: boolean;
                isAborted: boolean;
                abortUIConfig?: AbortUIConfig;
            } = {
                progress: inferenceQueueItem?.progress || lastProgress,
                onStop: handleStop,
                onRestart: handleStart,
                isStopped,
                isAborted
            };

            if (rotatingContent) {
                props.rotatingContent = rotatingContent;
            }

            // Determine appropriate UI config based on abort data
            if (isAborted) {
                props.abortUIConfig = getAbortUIConfig(inferenceQueueItem?.abortData);
            }

            return <BannerProgressHint {...props} />;
        }
        return null;
    }, [
        showHint,
        inferenceQueueItem?.progress,
        inferenceQueueItem?.abortData,
        lastProgress,
        rotatingContent,
        isStopped,
        isAborted,
        handleStop,
        handleStart
    ]);

    if (!hint) return null;

    return (
        <FloatingHint
            isVisible={!!hint}
            message={hint || ""}
            isDismissable={false}
            lightMode={true}
        />
    );
};
